import { setupClient } from "Infrastructure/Api/Api";
import { handleResponseError } from "Infrastructure/Api/ApiUtils";
import { JWT_TOKEN_KEY } from "Infrastructure/Constants/AuthConstants";
import { AuthProvider } from "Infrastructure/Contexts/AuthContext";
import { FilterProvider } from "Infrastructure/Contexts/FilterContext";
import { getTheme } from "Infrastructure/Themes/Themes";
import { CzechResourcesDictionary } from "Infrastructure/Translations/CzechResourcesDictionary";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import { ThemeProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { z } from "zod";
import { zodI18nMap } from "zod-i18n-map";
import { appRouter } from "./Infrastructure/Routing/AppRouter";

function App() {
  initSentry();

  const theme = getTheme();
  i18n.use(initReactI18next).init({
    resources: {
      cs: CzechResourcesDictionary,
    },
    lng: "cs",
    fallbackLng: "cs",
    interpolation: {
      escapeValue: false,
    },
  });

  z.setErrorMap(zodI18nMap);

  setupClient({
    apiUrl: `${import.meta.env.VITE_API_URL}`,
    jwtKey: () => {
      return localStorage.getItem(JWT_TOKEN_KEY);
    },
    requestMiddlewares: [],
    responseMiddlewares: [],
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      mutations: {
        onError: handleResponseError,
      },
    },
    queryCache: new QueryCache({
      onError: handleResponseError,
    }),
  });

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
          <ThemeProvider theme={theme}>
            <FilterProvider>
              <CssBaseline />
              <Toaster richColors position="top-left" />
              <RouterProvider router={appRouter} />
            </FilterProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
export default App;
